<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-configuration-intents-to-handoff">
    <div class="group-section">
      <div class="div-group-viewer-header div-group-name">
        <div class="title">Intents to handoff</div>

        <div class="options">
          <div class="col-languages">
            <el-select v-model="currLang" placeholder="Select" size="small" @change="handleFilter">
              <el-option
                v-for="lang in langs"
                :key="lang"
                :label="'Language ('+lang+')'"
                :value="lang">
              </el-option>
            </el-select>
          </div>
          <div class="col-search">
            <el-input placeholder="Search" v-model="keyword" class="input-with-select" size="small">
              <el-select v-model="currType" @change="handleFilter" slot="prepend" placeholder="Select">
                <el-option label="All Handoff Types" :value="-1"></el-option>
                <el-option label="Other" :value="0"></el-option>
                <el-option label="Customer requested" :value="1"></el-option>
                <el-option label="Programmed" :value="2"></el-option>
              </el-select>
              <el-button slot="append" icon="el-icon-search"></el-button>
            </el-input>
          </div>
          <div class="col-save" v-if="isRoleAllow([4201, 900002])">
             <el-button class="update-save-btn" size="small" :disabled="Object.keys(this.updatedData).length==0" :loading="loadingForUpdate" @click="handleUpdate">Update</el-button>
          </div>
          <div style="clear: both"/>
        </div>
      </div>
      <div class="div-content" v-loading="loading">
        <el-table
          v-if="dataList && dataList.length > 0"
          :data="dataList.filter(item => (!keyword || item.intent.toLowerCase().includes(keyword.toLowerCase())))" 
          :default-sort = "{prop: 'intent', order: 'ascending'}"
          height="calc(100% - 50px)">
          <el-table-column label="Intent" prop="intent" sortable></el-table-column>
          <el-table-column label="Handoff Type" prop="type" sortable align="right">
            <template slot-scope="scope">
              <div v-if="isRoleAllow([4201, 900002])" class="radio-div">
                <input type="radio" :name="scope.$index" value="0" :checked="scope.row.type==0" @change="handleTypeChange(scope.row, 0)" /> Other
                <input type="radio" :name="scope.$index" value="1" :checked="scope.row.type==1" @change="handleTypeChange(scope.row, 1)" /> Customer requested
                <input type="radio" :name="scope.$index" value="2" :checked="scope.row.type==2" @change="handleTypeChange(scope.row, 2)" /> Programmed
              </div>
              <div v-else>
                <div v-if="scope.row.type==0">Other</div>
                <div v-else-if="scope.row.type==1">Customer requested</div>
                <div v-else-if="scope.row.type==2">Programmed</div>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div v-else class="no-data">
          <div class="no-data-icon"></div>
          <div class="no-data-title">Sorry!</div>
          <div class="no-data-content">
            No data available yet for this brand or time period. Please use the global filter to select a brand and time period with data available.
          </div>
        </div>
      </div>
    </div>
  </div>

  
</template>

<script>
  import Vue from 'vue';
  import { mapGetters, mapActions } from 'vuex';
  import _ from 'lodash';

  export default Vue.extend({
    name: 'wy-analytics-configuration-intents-to-handoff',
    title: 'Configuration',
    props: ['controller'],
    data() {
      return {
        loading: false,
        loadingForUpdate: false,
        dataMap: false,
        dataMapOrigin: false,
        dataList: [],
        langs: null,
        currLang: "en",
        currType: -1,
        keyword: null,
        updatedData: [],
        preMsgObj: null
      }
    },

    computed: {
      ...mapGetters(['dataModel', 'isRoleAllow']),
    },
    
    methods: {
    ...mapActions(['fetchConfigIntentToHandoff', 'updateConfigIntentToHandoff']),
      
      async loadData() {
        this.loading = true;
        
        let result = await this.fetchConfigIntentToHandoff();
        if(result && result.data) {
          this.dataMap = result.data;
          this.dataMapOrigin = JSON.parse(JSON.stringify(this.dataMap));
          this.langs = Object.keys(this.dataMap).sort();
          this.currLang = this.langs.length > 0 ? this.langs[0] : null;
          if(this.dataMap && this.dataMap[this.currLang]) {
            this.dataList = this.dataMap[this.currLang];
          }
        }
        this.loading = false;
      },

      handleFilter() {
        if(this.dataMap && this.dataMap[this.currLang]) {
          if(this.currType > -1) {
            this.dataList = this.dataMap[this.currLang].filter(item => item.type==this.currType);
          }
          else {
            this.dataList = this.dataMap[this.currLang];
          }
        }
      },

      handleTypeChange(item, value) {
        item.type = value;

        this.updatedData = {};

        for(let lang of this.langs) {
          let dataOrigin = this.dataMapOrigin[lang];
          let dataCurr = this.dataMap[lang];
          let diffData = _.differenceWith(dataCurr, dataOrigin, _.isEqual);
          if(diffData.length > 0) {
            this.updatedData[lang] = diffData;
          }
        }
      },

      async handleUpdate() {
        this.loadingForUpdate = true;
        let result = await this.updateConfigIntentToHandoff({langs: this.updatedData});
        if(result.status == 200 && result.data) {
          this.updatedData = {};
          this.dataMapOrigin = JSON.parse(JSON.stringify(this.dataMap));
          this.langs = Object.keys(this.dataMap).sort();
          if(this.preMsgObj == null){
              this.preMsgObj = this.$message({
                dangerouslyUseHTMLString: false,
                duration: 1000,
                showClose: true,
                customClass: 'update-success-msg',
                message: 'Updated successfully',
                onClose: ()=>{
                  this.preMsgObj = null;
                }
              });
          }
        }
        else {
          if(this.preMsgObj == null){
            this.preMsgObj = this.$message({
              dangerouslyUseHTMLString: false,
              duration: 3000,
              showClose: true,
              customClass: 'update-failed-msg',
              message: 'Update failed',
              onClose: ()=>{
                this.preMsgObj = null;
              }
            });
          }
        }
        this.loadingForUpdate = false;
      }
    },

    mounted() {
      this.loadData();
    }
  });
</script>

<style lang="scss">
.wy-analytics-configuration-intents-to-handoff {
  width: 100%;
  height: 100%;
  box-sizing: border-box;

  .group-section {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    border: 1px solid #E8E8E8;
    border-radius: 15px;

    .div-group-viewer-header.div-group-name {
      font-size: 16px;
      font-weight: 500;
      padding: 14px 20px 12px 20px;
      border-bottom: 1px solid #E8E8E8;

      .options {
        padding-top: 20px;

        .el-select {
          width:  180px;
        }

        .col-languages {
          float: left;
          margin-right: 10px;
          input.el-input__inner {
            border: 1px solid #DCDFE6 !important
          }
        }

        .col-search {
          float: left;
          width: 600px;
          input.el-input__inner {
            border: 1px solid #DCDFE6 !important;
            border-width: 1px 1px 1px 0px !important;
          }          
        }

        .col-save {
          float: right;
          margin-left: 20px;
        }
        .update-save-btn {
          width: fit-content;
          height: fit-content;
          padding: 8px 16px 8px 16px;
          font-size: 15px;
          cursor: pointer;
          background: var(--site-button-primary-btn-background-primary-color);
          color: var(--site-button-primary-btn-text-primary-color) !important;
          border: 1px solid var(--site-button-primary-btn-hover-border-primary-color);
          font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
          border-radius: 5px;
          // margin-right: 15px;
          align-items: center;
          justify-content: center;
        }
        .update-save-btn:hover {
          border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
          background-color: var(--site-button-primary-btn-hover-background-primary-color) !important;
          border-radius: 5px;
          color: var(--site-button-primary-btn-text-primary-color) !important;
        }
        .update-save-btn:active {
          background-color: var(--site-button-primary-btn-pressed-background-primary-color) !important;
        }
        .update-save-btn:focus {
          border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
          border-radius: 5px;
          color: var(--site-button-primary-btn-text-primary-color) !important;
        }
        .update-save-btn.is-disabled {
          background: var(--site-button-primary-btn-disabled-background-primary-color);
          cursor: not-allowed;
        }
        .update-save-btn.is-disabled:hover {
          border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
          background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
          border-radius: 5px;
          color: var(--site-button-primary-btn-text-primary-color) !important;
        }
        .update-save-btn.is-disabled:active {
          background: var(--site-button-primary-btn-disabled-background-primary-color) !important;
        }
        .update-save-btn.is-disabled:focus {
          border: 1px var(--site-button-primary-btn-hover-border-primary-color) solid !important;
          border-radius: 5px;
          color: var(--site-button-primary-btn-text-primary-color) !important;
        }
      }
    }

    .div-content {
      box-sizing: border-box;
      height: calc(100% - 42px);
      padding: 05px 25px 25px 25px;
      overflow-y: hidden;

      .el-table {
        height: 400px;
        .radio-div {
          input {
            accent-color: #006FE7;
          }
        }
      }

      .el-table__empty-text {
        line-height: 24px !important;
      }
    }
  }

  .no-data {
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    .no-data-icon {
      display: block;
      border: none;
      height: 155px;
      width: 155px;
      background-position-x: 50%;
      background-repeat: no-repeat;
      background-image: url(../../../assets/icons/icon_no_data_new.svg);
      margin-bottom: 10px;
    }
    .no-data-title {
      font-size: 21px !important;
      color: var(--site-font-tertiary-color) !important;
      font-weight: 600;
      margin-bottom: 10px;
    }
    .no-data-content {
      font-size: 14px;
      color: #4B5158 !important;
      line-height: 22px;
      text-align: center;
      width: 415px;
    }
  }
}
</style>